import React from 'react'
import { Button, Modal } from '@/shared/ui'
import type { Plan } from '../types'
import type { CryptoAsset } from '@/modules/crypto-assets/services/cryptoAssetsService'
import { listCryptoAssetsBySymbols, listCryptoAssetsPaged } from '@/modules/crypto-assets/services/cryptoAssetsService'
import { getPlanAssetPolicyPublic } from '../services/planAssetPolicyService'

type Props = {
  open: boolean
  plan: Plan | null
  onClose: () => void
}

export default function PlanAssetsModal({ open, plan, onClose }: Props) {
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [mode, setMode] = React.useState<'all' | 'limited'>('all')
  const [assets, setAssets] = React.useState<CryptoAsset[]>([])

  async function loadAllActiveAssets(): Promise<CryptoAsset[]> {
    const pageSize = 100
    let page = 0
    const all: CryptoAsset[] = []
    const maxPages = 10
    while (page < maxPages) {
      const { items } = await listCryptoAssetsPaged({ page, pageSize, onlyActive: true })
      all.push(...items)
      if (items.length < pageSize) break
      page += 1
    }
    return all
  }

  React.useEffect(() => {
    let mounted = true
    async function load() {
      if (!open || !plan?.id) {
        if (mounted) {
          setAssets([])
          setMode('all')
          setError(null)
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const policy = await getPlanAssetPolicyPublic(plan.id)
        if (!mounted) return
        if (!policy || policy.mode === 'all') {
          setMode('all')
          const list = await loadAllActiveAssets()
          if (!mounted) return
          setAssets(list)
          return
        }
        if (policy.mode === 'limited' && policy.allowed_symbols?.length) {
          setMode('limited')
          const list = await listCryptoAssetsBySymbols(policy.allowed_symbols)
          if (!mounted) return
          setAssets(list)
          return
        }
        // policy.mode === 'limited' mas sem simbolos configurados
        setMode('limited')
        setAssets([])
      } catch (e: any) {
        if (!mounted) return
        const msg = String(e?.message || '')
        if (msg.includes('plan_asset_policies') || msg.includes('get_plan_asset_policy_public')) {
          setError(
            'Recurso de moedas por plano ainda n\u00e3o est\u00e1 habilitado no banco (tabela ou RPC ausente).'
          )
        } else {
          setError('Falha ao carregar as moedas deste plano.')
        }
        setAssets([])
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [open, plan?.id])

  const title = plan ? `Moedas do plano ${plan.name}` : 'Moedas do plano'

  return (
    <Modal
      open={open}
      onOpenChange={(v)=> { if (!v) onClose() }}
      className="max-w-3xl"
    >
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">{title}</div>
          <div className="text-xs text-foreground/70">
            {mode === 'limited'
              ? 'Veja as moedas que fazem parte deste plano na p\u00e1gina de Oportunidades.'
              : 'Este plano n\u00e3o possui limita\u00e7\u00e3o de moedas e considera todas as moedas ativas do sistema.'}
          </div>
        </div>
        <Button variant="outline" onClick={onClose}>
          Fechar
        </Button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-foreground/70">Carregando moedas...</div>
      ) : error ? (
        <div className="py-6 text-center text-sm text-destructive-foreground">{error}</div>
      ) : !assets.length ? (
        <div className="py-6 text-center text-sm text-foreground/70">
          {mode === 'limited'
            ? 'Nenhuma moeda foi configurada para este plano ainda.'
            : 'Nenhum ativo ativo foi encontrado no sistema.'}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {assets.map((asset) => {
            const sym = String(asset.symbol || '').toUpperCase()
            const label = asset.name || asset.base_asset || sym
            const baseInitials = String(asset.base_asset || sym).slice(0, 4).toUpperCase()
            return (
              <div
                key={asset.id || sym}
                className="flex flex-col items-center rounded-lg border border-border/60 bg-muted/20 p-3 text-center"
              >
                <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-background/60">
                  {asset.logo_url ? (
                    <img
                      src={asset.logo_url}
                      alt={sym}
                      className="h-8 w-8 rounded-full object-contain"
                    />
                  ) : (
                    <span className="text-xs font-semibold text-foreground/70">{baseInitials}</span>
                  )}
                </div>
                <div className="w-full truncate text-xs font-semibold text-foreground">
                  {label}
                </div>
                <div className="mt-0.5 text-[11px] text-foreground/60">{sym}</div>
              </div>
            )
          })}
        </div>
      )}
    </Modal>
  )
}


